import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import {
  Briefcase, Users, Receipt, TrendingUp, CalendarClock,
  AlertTriangle, Activity, Plus, ChevronRight, Bell, CheckCircle2
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid
} from 'recharts';

/* ─── Formateo ────────────────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS', maximumFractionDigits: 0,
  }).format(n || 0);

const fmtFecha = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('es-AR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return s; }
};

const fmtHora = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

/* ─── Colores para dona ─────────────────────────────────────────── */
const DONA_COLORS = ['#C9A44C', '#E0C878', '#997A00', '#B8860B', '#886B2A', '#665020'];

/* ─── Sub-componentes ────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, sub, color = 'gold' }) {
  const col = color === 'green' ? 'text-green-400' : color === 'red' ? 'text-red-400' : 'text-[#C9A44C]';
  return (
    <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5 hover:border-[#C9A44C]/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${col}`} />
        {sub && <span className="text-[10px] text-[#666] uppercase tracking-wide">{sub}</span>}
      </div>
      <div className="text-2xl mb-1 font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
        {value ?? '—'}
      </div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#888]">{label}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-3">{children}</h3>
  );
}

/* ─── Tooltip custom recharts ─────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-[rgba(201,164,76,0.2)] rounded-lg px-3 py-2 text-xs">
      <div className="text-[#C9A44C] mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="text-[#F8F6F0]">{p.name}: {p.value}</div>
      ))}
    </div>
  );
};

/* ─── Dashboard principal ─────────────────────────────────────── */
export default function Dashboard() {
  const navigate = useNavigate();

  const [metricas, setMetricas] = useState(null);
  const [tasa, setTasa] = useState(null);
  const [audiencias, setAudiencias] = useState([]);
  const [vencimientos, setVencimientos] = useState([]);
  const [movRecientes, setMovRecientes] = useState([]);
  const [productividad, setProductividad] = useState([]);
  const [casosPorEstado, setCasosPorEstado] = useState([]);
  const [notis, setNotis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [met, tas, aud, venc, movs, prod, estados, noti] = await Promise.allSettled([
          api.get('/api/admin/metricas'),
          api.get('/api/reportes/dashboard/tasa-cobranza'),
          api.get('/api/reportes/dashboard/audiencias-proximas?dias=30'),
          api.get('/api/reportes/dashboard/vencimientos-criticos?dias=7'),
          api.get('/api/movimientos/recientes?limit=10'),
          api.get('/api/reportes/dashboard/productividad-abogados'),
          api.get('/api/reportes/dashboard/casos-por-estado'),
          api.get('/api/notificaciones/pendientes'),
        ]);
        if (met.status === 'fulfilled') setMetricas(met.value.data);
        if (tas.status === 'fulfilled') setTasa(tas.value.data);
        if (aud.status === 'fulfilled') setAudiencias(aud.value.data.items || []);
        if (venc.status === 'fulfilled') setVencimientos(venc.value.data.items || []);
        if (movs.status === 'fulfilled') setMovRecientes(movs.value.data || []);
        if (prod.status === 'fulfilled') setProductividad(prod.value.data.data || []);
        if (estados.status === 'fulfilled') setCasosPorEstado(estados.value.data.data || []);
        if (noti.status === 'fulfilled') setNotis(noti.value.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* Gráfico barras — producción por abogado */
  const dataBarras = productividad.map((a) => ({
    name: (a.abogado || '').split(' ')[0],
    causas: a.total_casos || 0,
    mes: a.casos_nuevos_mes || 0,
  }));

  /* Gráfico dona — estados */
  const dataDona = casosPorEstado.map((e, i) => ({
    name: (e.estado || 'Sin estado').replace('_', ' '),
    value: Number(e.cantidad) || 0,
    color: DONA_COLORS[i % DONA_COLORS.length],
  }));

  /* Centro de notificaciones — mismo origen que la campana */
  const notiVencidos = notis?.vencidos || [];
  const notiPorVencer = notis?.por_vencer || [];
  const notiEventos = notis?.eventos_proximos || [];
  const notiTotal = notis?.total || 0;
  const saldo = (h) => (h.monto || 0) - (h.monto_pagado || 0);

  return (
    <DashboardLayout title="Panel de Control">

      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={Receipt}
          label="Facturación mensual"
          value={metricas?.facturacion_mensual ?? '—'}
        />
        <KpiCard
          icon={Briefcase}
          label="Casos totales"
          value={loading ? '—' : (metricas?.casos_totales ?? 0)}
        />
        <KpiCard
          icon={TrendingUp}
          label="Cobrado total"
          value={tasa ? fmt(tasa.cobrado) : '—'}
          color="green"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Pendiente cobro"
          value={tasa ? fmt(tasa.pendiente) : '—'}
          sub={tasa ? `${tasa.tasa_pct}% cobrado` : ''}
          color="red"
        />
      </div>

      {/* ── Centro de notificaciones ─────────────────────────── */}
      <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#C9A44C]" />
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C]">Centro de notificaciones</h3>
          </div>
          {notiTotal > 0 && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#C9A44C]/15 text-[#C9A44C]">
              {notiTotal} pendiente{notiTotal === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {notiTotal === 0 ? (
          <div className="text-center py-8 text-[#555]">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40 text-green-400" />
            <p className="text-xs">Todo al día — sin vencimientos ni eventos próximos</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-5">

            {/* Honorarios vencidos */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-[10px] uppercase tracking-wide text-red-400">Honorarios vencidos</span>
                <span className="text-[10px] text-[#666]">({notiVencidos.length})</span>
              </div>
              {notiVencidos.length === 0 ? (
                <p className="text-[11px] text-[#555] px-1">Ninguno</p>
              ) : (
                <div className="space-y-2">
                  {notiVencidos.slice(0, 3).map((h) => (
                    <button key={h.id} onClick={() => navigate('/facturacion')}
                      className="w-full text-left flex items-center justify-between p-2.5 rounded-lg bg-red-500/[0.06] hover:bg-red-500/[0.1] border border-red-500/10 transition-all">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{h.cliente?.trim() || 'Honorario'}</div>
                        <div className="text-[10px] text-[#888]">Venció {fmtFecha(h.fecha_vencimiento)}</div>
                      </div>
                      <div className="text-xs text-red-400 ml-2 flex-shrink-0">{fmt(saldo(h))}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Pagos por vencer */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Receipt className="w-3.5 h-3.5 text-[#C9A44C]" />
                <span className="text-[10px] uppercase tracking-wide text-[#C9A44C]">Pagos por vencer (7d)</span>
                <span className="text-[10px] text-[#666]">({notiPorVencer.length})</span>
              </div>
              {notiPorVencer.length === 0 ? (
                <p className="text-[11px] text-[#555] px-1">Ninguno</p>
              ) : (
                <div className="space-y-2">
                  {notiPorVencer.slice(0, 3).map((h) => (
                    <button key={h.id} onClick={() => navigate('/facturacion')}
                      className="w-full text-left flex items-center justify-between p-2.5 rounded-lg bg-[#C9A44C]/[0.06] hover:bg-[#C9A44C]/[0.1] border border-[#C9A44C]/10 transition-all">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{h.cliente?.trim() || 'Honorario'}</div>
                        <div className="text-[10px] text-[#888]">Vence {fmtFecha(h.fecha_vencimiento)}</div>
                      </div>
                      <div className="text-xs text-[#C9A44C] ml-2 flex-shrink-0">{fmt(saldo(h))}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Eventos próximos */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <CalendarClock className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] uppercase tracking-wide text-blue-400">Eventos próximos (48h)</span>
                <span className="text-[10px] text-[#666]">({notiEventos.length})</span>
              </div>
              {notiEventos.length === 0 ? (
                <p className="text-[11px] text-[#555] px-1">Ninguno</p>
              ) : (
                <div className="space-y-2">
                  {notiEventos.slice(0, 3).map((ev) => (
                    <button key={ev.id} onClick={() => navigate('/agenda')}
                      className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg bg-blue-500/[0.06] hover:bg-blue-500/[0.1] border border-blue-500/10 transition-all">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{ev.titulo}</div>
                        <div className="text-[10px] text-[#888]">{fmtFecha(ev.inicio)} · {fmtHora(ev.inicio)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* ── Gráficos ─────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">

        {/* Barras: producción por abogado — una barra por abogado */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <SectionTitle>Producción por abogado</SectionTitle>
          {dataBarras.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-[#555] text-sm">Sin datos aún</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dataBarras} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                style={{ background: 'transparent' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(201,164,76,0.06)' }} />
                <Bar dataKey="causas" name="Causas totales" fill="#C9A44C"
                  radius={[5, 5, 0, 0]} barSize={32}
                  label={{ position: 'top', fill: '#C9A44C', fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Dona: estado de causas */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <SectionTitle>Estado de causas</SectionTitle>
          {dataDona.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-[#555] text-sm">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={dataDona}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {dataDona.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => <span style={{ fontSize: 11, color: '#aaa' }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Tasa de cobranza (barra de progreso) ──────────────── */}
      {tasa && (
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5 mb-6">
          <div className="flex justify-between items-center mb-3">
            <SectionTitle>Tasa de cobranza global</SectionTitle>
            <span className="text-[#C9A44C] text-sm font-medium">{tasa.tasa_pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${tasa.tasa_pct}%`, background: 'linear-gradient(90deg, #C9A44C, #E0C878)' }}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-3 text-center">
            <div className="min-w-0">
              <div className="text-xs sm:text-sm truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{fmt(tasa.facturado)}</div>
              <div className="text-[10px] text-[#666] uppercase">Facturado</div>
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-green-400 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{fmt(tasa.cobrado)}</div>
              <div className="text-[10px] text-[#666] uppercase">Cobrado</div>
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-red-400 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{fmt(tasa.pendiente)}</div>
              <div className="text-[10px] text-[#666] uppercase">Pendiente</div>
            </div>
          </div>
        </div>
      )}

      {/* ── 3 columnas: Audiencias / Vencimientos / Movimientos ── */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">

        {/* Audiencias próximas */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>Audiencias próximas</SectionTitle>
            <button onClick={() => navigate('/agenda')} className="text-[#C9A44C] hover:text-[#E0C878]">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {audiencias.length === 0 ? (
            <div className="text-center py-8 text-[#555]">
              <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">Sin audiencias en 30 días</p>
            </div>
          ) : (
            <div className="space-y-2">
              {audiencias.slice(0, 5).map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate('/expedientes')}
                  className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-all"
                >
                  <div className="flex-shrink-0 text-center w-10">
                    <div className="text-lg leading-none text-[#C9A44C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {new Date(a.fecha_programada).getDate()}
                    </div>
                    <div className="text-[9px] text-[#888] uppercase">
                      {new Date(a.fecha_programada).toLocaleString('es-AR', { month: 'short' })}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{a.titulo || a.caratula}</div>
                    <div className="text-[10px] text-[#888] truncate">{fmtHora(a.fecha_programada)} · {a.caratula}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Vencimientos críticos */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>Vencimientos críticos</SectionTitle>
            <button onClick={() => navigate('/facturacion')} className="text-[#C9A44C] hover:text-[#E0C878]">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {vencimientos.length === 0 ? (
            <div className="text-center py-8 text-[#555]">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">Sin vencimientos próximos</p>
            </div>
          ) : (
            <div className="space-y-2">
              {vencimientos.slice(0, 5).map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{v.cliente_nombre}</div>
                    <div className="text-[10px] text-[#888]">{fmtFecha(v.fecha_vencimiento)}</div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <div className="text-xs">{fmt((v.monto || 0) - (v.monto_pagado || 0))}</div>
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full ${
                      v.criticidad === 'vencido'
                        ? 'bg-red-500/15 text-red-400'
                        : 'bg-[#C9A44C]/15 text-[#C9A44C]'
                    }`}>
                      {v.criticidad}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Movimientos recientes */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>Actividad reciente</SectionTitle>
            <button onClick={() => navigate('/expedientes')} className="text-[#C9A44C] hover:text-[#E0C878]">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {movRecientes.length === 0 ? (
            <div className="text-center py-8 text-[#555]">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">Sin actividad reciente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {movRecientes.slice(0, 6).map((m) => (
                <div
                  key={m.id}
                  className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/[0.02] border border-white/5"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C9A44C] mt-1.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs truncate">{m.caratula || m.titulo || m.tipo}</div>
                    <div className="text-[10px] text-[#888]">
                      {m.tipo} · {fmtFecha(m.fecha)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Acciones rápidas ─────────────────────────────────── */}
      <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
        <SectionTitle>Acciones rápidas</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Nuevo cliente', icon: Users, path: '/clientes' },
            { label: 'Nuevo expediente', icon: Briefcase, path: '/expedientes' },
            { label: 'Registrar pago', icon: Receipt, path: '/facturacion' },
            { label: 'Nuevo evento', icon: CalendarClock, path: '/agenda' },
          ].map(({ label, icon: Icon, path }) => (
            <button
              key={label}
              onClick={() => navigate(path)}
              className="flex items-center gap-2.5 bg-white/[0.02] border border-white/10 rounded-xl p-3.5 hover:border-[#C9A44C]/30 hover:bg-white/[0.05] transition-all text-left"
            >
              <div className="w-9 h-9 rounded-lg bg-[#C9A44C]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[#C9A44C]" />
              </div>
              <span className="text-xs text-[#ccc]">{label}</span>
            </button>
          ))}
        </div>
      </div>

    </DashboardLayout>
  );
}
